import { CreatorBrand } from 'constants/brands';
import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import {
    Box,
    PlayFilled,
    PauseFilled,
    useBreakpoint,
    Group,
    IconButton,
} from '@landr/maestro';
import { CreatorDownloadSource, CreatorCollectionPlaybackMeta } from 'types';
import { DownloadType, RoutePaths } from 'types/enums';
import { getLinearToDecibel } from 'helpers/audio';
import { Hidden } from 'components/CommonUi/Hidden';
import { useOnboardingContext } from 'contexts/OnboardingContext/OnboardingContext';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { CreatorSampleType } from 'controllers/business/Creator/Creator.types';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { Styled } from '../Creator.styled';
import SamplesCount from '../SamplesCount';
import { CreatorHeaderMobileMenu } from './CreatorHeaderMobileMenu/CreatorHeaderMobileMenu';
import { BpmControlPopover } from './BpmControlPopover';
import { KeyControlPopover } from './KeyControlPopover';
import { CreatorHeaderActionsGroup } from './CreatorHeaderActionsGroup';
import HeaderStyles from './CreatorHeader.styled';
import CreatorLogo from './CreatorLogo';
export interface CreatorHeaderProps {
    onClickPlay: () => void;
    onApplyBpm: (bmp: number) => void;
}

export const CreatorHeader: React.FC<CreatorHeaderProps> = ({
    onClickPlay,
    onApplyBpm,
}) => {
    const { toggleCreator, state, getTrackGain } = useCreatorContext();
    const { requestOnboarding, WalkThrough } = useOnboardingContext();

    const toLocalizedPath = useToLocalizedPath();

    const {
        keyRoot,
        bpm,
        keyQuality,
        normalizedRootValue,
        isCreatorOpen,
        isUiPaused,
        isSessionProcessing,
        samplesInSession,
    } = state;

    const sampleIds = [...samplesInSession.keys()];
    const samples = [...samplesInSession.values()];
    const samplesCount = sampleIds.length;

    const areActionsDisabled = samplesCount === 0 || isSessionProcessing;

    const creatorDownloadSource: CreatorDownloadSource = {
        type: DownloadType.CreatorMulti,
        targetMeta: {
            targetBpm: state.bpm,
            targetKeyRoot: state.keyRoot,
            targetKeyQuality: state.keyQuality,
        },
        samples: [...samplesInSession.values()].map(
            (sample: CreatorSampleType) => ({
                guid: sample.guid,
                gainDb: getLinearToDecibel(getTrackGain(sample.guid)),
                semitoneShift: sample.semitoneShift,
                timeFactor: sample.timeFactor,
            }),
        ),
        name: 'Creator',
        isDownloaded: false,
    };

    const handleOnTriggerClick = (event?: React.MouseEvent): void => {
        event && event.stopPropagation();
        toggleCreator();
    };

    useEffect(() => {
        if (isCreatorOpen && !areActionsDisabled) {
            requestOnboarding([WalkThrough.Creator]);
        }
    }, [
        isCreatorOpen,
        areActionsDisabled,
        requestOnboarding,
        WalkThrough.Creator,
    ]);

    let playbackMeta: CreatorCollectionPlaybackMeta | undefined = undefined;

    if (keyRoot && keyQuality) {
        playbackMeta = {
            bpm,
            keyRoot: keyRoot,
            keyQuality: keyQuality,
        };
    }

    const isSmallMobile = useBreakpoint('sm');

    const responsiveGutter = isSmallMobile ? 'xxs' : 'xs';

    return (
        <Box
            data-component="CreatorHeader"
            display="flex"
            flexDirection="column"
            alignItems="space-between"
        >
            <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Group
                    display="flex"
                    alignItems="center"
                    gutter={responsiveGutter}
                    onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                >
                    <Group.Item>
                        <CreatorLogo
                            link={toLocalizedPath(
                                `/${RoutePaths.CreatorBeatMaker}`,
                            )}
                        />
                    </Group.Item>
                    <Group.Item>
                        <IconButton
                            variant="secondary"
                            isRounded
                            size="sm"
                            icon={isUiPaused ? PlayFilled : PauseFilled}
                            isLoading={isSessionProcessing}
                            isDisabled={areActionsDisabled}
                            onClick={onClickPlay}
                            label={t`Play`}
                        />
                    </Group.Item>
                    <Group.Item>
                        <HeaderStyles.CreatorHeaderWrapper data-component="BpmAndKeyControl">
                            <Group
                                display="flex"
                                alignItems="center"
                                gutter={responsiveGutter}
                                onClick={(e: React.MouseEvent): void =>
                                    e.stopPropagation()
                                }
                            >
                                <Group.Item>
                                    <BpmControlPopover
                                        bpm={state.bpm}
                                        isDisabled={areActionsDisabled}
                                        onApply={onApplyBpm}
                                    />
                                </Group.Item>
                                <Group.Item>
                                    <KeyControlPopover
                                        keyQuality={keyQuality}
                                        normalizedRootValue={
                                            normalizedRootValue
                                        }
                                        isDisabled={areActionsDisabled}
                                    />
                                </Group.Item>
                            </Group>
                        </HeaderStyles.CreatorHeaderWrapper>
                    </Group.Item>
                    <Hidden as={Group.Item} at="md" ml="xxs">
                        <SamplesCount samplesCount={samplesCount} />
                    </Hidden>
                </Group>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {samplesCount > 0 && (
                        <>
                            <HeaderStyles.DesktopOnlyGroup
                                gutter="xs"
                                onClick={(e: React.MouseEvent): void =>
                                    e.stopPropagation()
                                }
                            >
                                <CreatorHeaderActionsGroup
                                    creatorDownloadSource={
                                        creatorDownloadSource
                                    }
                                    samples={samples}
                                    playbackMeta={playbackMeta}
                                    isDisabled={areActionsDisabled}
                                />
                            </HeaderStyles.DesktopOnlyGroup>
                            <HeaderStyles.MobileOnlyGroup
                                onClick={(e: React.MouseEvent): void =>
                                    e.stopPropagation()
                                }
                            >
                                <CreatorHeaderMobileMenu
                                    samples={samples}
                                    creatorDownloadSource={
                                        creatorDownloadSource
                                    }
                                    playbackMeta={playbackMeta}
                                    isDisabled={areActionsDisabled}
                                />
                            </HeaderStyles.MobileOnlyGroup>
                            <HeaderStyles.Separator />
                            <Styled.CreatorTrigger
                                aria-label={t`Toggle ${CreatorBrand}`}
                                isCreatorOpen={isCreatorOpen}
                                onClick={handleOnTriggerClick}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CreatorHeader;
